/**
 * tax-certificate.tsx — Chọn kỳ (tháng / quý / năm) + mở chứng từ thu nhập / khấu trừ thuế TNCN (E14).
 * Chứng từ là trang HTML do hệ thống lập (in → lưu PDF); dùng chung cho cổng đối tác và Platform.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { FileText, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { defaultPeriod, periodLabel, periodParams, type PeriodKind, type PeriodSel } from '@/lib/affiliate'

const SELECT = 'h-9 rounded-md border bg-background px-2 text-sm'

export function PeriodPicker({ value, onChange }: { value: PeriodSel; onChange: (p: PeriodSel) => void }) {
  const { t } = useTranslation('growth')
  const thisYear = new Date().getFullYear()
  const years = Array.from({ length: Math.max(1, thisYear - 2024 + 1) }, (_, i) => thisYear - i)
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select aria-label={t('taxCertificate.periodKind')} className={SELECT} value={value.kind} onChange={(e) => onChange({ ...value, kind: e.target.value as PeriodKind })}>
        <option value="month">{t('taxCertificate.month')}</option>
        <option value="quarter">{t('taxCertificate.quarter')}</option>
        <option value="year">{t('taxCertificate.year')}</option>
      </select>
      {value.kind === 'month' && (
        <select aria-label={t('taxCertificate.month')} className={SELECT} value={value.month} onChange={(e) => onChange({ ...value, month: Number(e.target.value) })}>
          {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{t('taxCertificate.monthN', { n: i + 1 })}</option>)}
        </select>
      )}
      {value.kind === 'quarter' && (
        <select aria-label={t('taxCertificate.quarter')} className={SELECT} value={value.quarter} onChange={(e) => onChange({ ...value, quarter: Number(e.target.value) })}>
          {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{t('taxCertificate.quarterN', { n: q })}</option>)}
        </select>
      )}
      <select aria-label={t('taxCertificate.year')} className={SELECT} value={value.year} onChange={(e) => onChange({ ...value, year: Number(e.target.value) })}>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  )
}

/**
 * Mở HTML ở tab mới (mở tab trước khi tải để không bị chặn popup).
 *
 * vbsec L43: `noopener` — tab mới không giữ `window.opener` trỏ về ứng dụng. Nội dung (blob) cùng origin
 * với ứng dụng nên nếu thiếu cờ này, một script lỡ chạy được trong tab đó vẫn điều hướng/đọc được cửa sổ
 * ứng dụng qua `opener` (phòng ngừa thêm — tài liệu phía server đã tự "sandbox" bằng CSP, xem
 * `renderTaxCertificateHtml`).
 */
export async function openHtml(load: () => Promise<string>): Promise<void> {
  const win = window.open('', '_blank', 'noopener')
  try {
    const html = await load()
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }))
    if (win) win.location.href = url
    else window.location.assign(url)
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (e) {
    win?.close()
    throw e
  }
}

export function TaxCertificateCard({ load, errorText }: { load: (params: Record<string, string>) => Promise<string>; errorText: (e: unknown) => string }) {
  const { t } = useTranslation('growth')
  const [p, setP] = useState<PeriodSel>(() => defaultPeriod())
  const [busy, setBusy] = useState(false)
  const open = async () => {
    setBusy(true)
    try {
      await openHtml(() => load(periodParams(p)))
    } catch (e) {
      toast.error(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card className="space-y-3 p-5">
      <p className="font-medium">{t('taxCertificate.title')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <PeriodPicker value={p} onChange={setP} />
        <Button variant="outline" onClick={open} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {t('taxCertificate.viewPrint', { period: periodLabel(p) })}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('taxCertificate.note')}
      </p>
    </Card>
  )
}
